/**
 * Service Worker Registration — handles registration, update detection,
 * and forced activation for CW POS offline caching.
 *
 * When a new SW version is detected:
 * - If no active transaction (no cart items), auto-reload to pick up new code
 * - If transaction in progress, defer reload until cart is empty
 *
 * Update detection:
 * - 5-minute polling via registration.update()
 * - Startup freshness check: compares running version against sw-manifest.json
 * - updateViaCache: "none" ensures browser always fetches sw.js from network
 */

import { trace } from "./utils/debugTrace";
import useSessionStore from "./stores/useSessionStore";

const UPDATE_POLL_INTERVAL = 5 * 60 * 1000;

let pendingUpdate = false;

function hasActiveTransaction() {
  try {
    const path = window.location.pathname;
    if (path === "/login" || path === "/sync" || path === "/open-till" || path === "/open-till-component") {
      return true;
    }

    const cartObj = localStorage.getItem("cartObj");
    if (!cartObj) return false;
    const cart = JSON.parse(cartObj);
    return Array.isArray(cart?.items) && cart.items.length > 0;
  } catch {
    return false;
  }
}

function applyUpdate(registration) {
  if (registration?.waiting) {
    trace("SWRegister", "applyUpdate → posting SKIP_WAITING", { pathname: window.location.pathname });
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }
}

function getRunningVersion() {
  try {
    return import.meta.env.VITE_APP_VERSION || null;
  } catch {
    return null;
  }
}

// Exposed for support: package.json's version is manually bumped (weeks can
// pass between bumps), so it can't tell two builds apart. window.__CW_BUILD__
// carries the commit SHA baked in at build time — run it in DevTools console
// on any live till and compare against `git log` to know exactly what's
// actually running, instead of inferring from deploy timing.
if (typeof window !== "undefined") {
  try {
    window.__CW_BUILD__ = {
      version: import.meta.env.VITE_APP_VERSION || null,
      gitSha: import.meta.env.VITE_GIT_SHA || null,
      builtAt: import.meta.env.VITE_BUILD_TIME || null,
    };
  } catch {
    // never block app startup over a diagnostics field
  }
}

async function checkVersionFreshness(registration) {
  try {
    const runningVersion = getRunningVersion();
    if (!runningVersion) return;

    const res = await fetch("./sw-manifest.json?_t=" + Date.now(), { cache: "no-store" });
    if (!res.ok) return;
    const manifest = await res.json();
    if (manifest.version && manifest.version !== runningVersion) {
      trace("SWRegister", "version mismatch on startup → registration.update()", {
        runningVersion,
        manifestVersion: manifest.version,
      });
      registration.update().catch(() => {});
    }
  } catch {
    // Network error — skip freshness check
  }
}

// Hostnames where running the Vite dev server (import.meta.env.DEV === true)
// is expected. Anything else is a deployed-looking origin — if DEV is still
// true there, this is almost certainly a misconfigured deployment serving
// `vite`/`npm start` instead of a production build, and offline support is
// silently unavailable (BUG-1700: SW registration below never runs).
const LOCAL_DEV_HOSTNAMES = ["localhost", "127.0.0.1", "0.0.0.0"];

function warnIfDevModeOnRemoteOrigin() {
  if (typeof window === "undefined") return;
  const hostname = window.location.hostname;
  if (LOCAL_DEV_HOSTNAMES.includes(hostname)) return;

  console.error(
    `[BUG-1700] [SW] Running in Vite dev mode on non-local origin "${hostname}" — ` +
      "service worker registration is skipped, so there is no offline fallback. " +
      "This deployment should be serving a production build (npm run build), not the dev server."
  );
}

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  if (import.meta.env.DEV) {
    warnIfDevModeOnRemoteOrigin();
    return;
  }

  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("./sw.js", {
        scope: "./",
        updateViaCache: "none",
      });

      trace("SWRegister", "registered", { scope: registration.scope, pathname: window.location.pathname });

      checkVersionFreshness(registration);

      setInterval(() => {
        registration.update().catch(() => {});
      }, UPDATE_POLL_INTERVAL);

      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        trace("SWRegister", "updatefound", { pathname: window.location.pathname });

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            trace("SWRegister", "new worker installed", {
              pathname: window.location.pathname,
              hasActiveTransaction: hasActiveTransaction(),
              locked: useSessionStore.getState().locked,
            });
            if (!hasActiveTransaction()) {
              applyUpdate(registration);
            } else {
              pendingUpdate = true;
            }
          }
        });
      });

      // BUG-2066/2067/2068 diagnostics: on the POS-side trace, handleLogin's
      // window.location.assign("/sign-in?...&readOnly=true") sometimes lands
      // on bare "/" instead — with no server redirect and no shell-side
      // involvement found. This is the one remaining candidate: if a
      // controllerchange lands in the gap between assign() being called and
      // that navigation committing, this reload() (on the still-alive old
      // document) can race and win, reloading the CURRENT pre-navigation URL
      // instead. Logged so the next repro either catches this red-handed or
      // rules it out — remove once root cause is confirmed.
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        const path = window.location.pathname;
        trace("SWRegister", "controllerchange fired", { path, locked: useSessionStore.getState().locked });
        if (path === "/login" || path === "/sync" || path === "/open-till" || path === "/open-till-component") {
          // Don't reload during login/sync flows — mark pending so the update
          // applies after the flow completes (via checkPendingSwUpdate).
          // Previously this cleared pendingUpdate, silently dropping the update.
          pendingUpdate = true;
          trace("SWRegister", "controllerchange deferred (exempt path)", { path });
          return;
        }
        trace("SWRegister", "controllerchange reloading now", { path });
        window.location.reload();
      });

      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type === "SW_UPDATED") {
        }
      });
    } catch (err) {
      console.warn("[SW] Registration failed:", err.message);
    }
  });
}

export function checkPendingSwUpdate() {
  if (!pendingUpdate) return;
  if (hasActiveTransaction()) return;

  pendingUpdate = false;
  navigator.serviceWorker?.getRegistration().then((reg) => {
    if (reg?.waiting) {
      applyUpdate(reg);
    }
  });
}
